import { dateToIso, isoToDate } from "@/shared/components/forms/colocaDayPicker.shared";
import { MONEY_INPUT_PATTERN } from "@/shared/utils/decimal";

import type { ListPaymentMovementsParams } from "../services/paymentsWallets.service";
import type { LedgerDirection, MovementIncidentFilter } from "../types/payments-wallets.types";

/**
 * Filters of the staff movements tab. They live in the URL under an `m_` prefix, because the wallets tab next
 * to it already owns `q`, `currency`, `min`, `max` and `page` on the same page.
 */
export interface WalletMovementsFilters {
  search: string;
  organizationId: string | null;
  currency: string | null;
  rail: string | null;
  direction: LedgerDirection | null;
  operationStatus: string | null;
  incident: MovementIncidentFilter | null;
  /** Local days, `YYYY-MM-DD`, or "". */
  from: string;
  to: string;
  minAmount: string;
  maxAmount: string;
}

export const EMPTY_MOVEMENTS_FILTERS: WalletMovementsFilters = {
  search: "",
  organizationId: null,
  currency: null,
  rail: null,
  direction: null,
  operationStatus: null,
  incident: null,
  from: "",
  to: "",
  minAmount: "",
  maxAmount: "",
};

export const MOVEMENTS_URL_PARAMS = {
  search: "m_q",
  organizationId: "m_org",
  currency: "m_cur",
  rail: "m_rail",
  direction: "m_dir",
  operationStatus: "m_status",
  incident: "m_incident",
  from: "m_from",
  to: "m_to",
  minAmount: "m_min",
  maxAmount: "m_max",
  page: "m_page",
} as const;

/** The open movement's leg id. */
export const MOVEMENT_OPEN_PARAM = "m_open";

/** The backend caps `search` at this many characters (`PaymentMovementsQueryDto`). */
export const MOVEMENTS_SEARCH_MAX = 120;

/** The rails a movement can be filtered by, in the order staff look for them. */
export const MOVEMENT_RAILS = [
  "BREB",
  "TRANSFIYA",
  "PSE",
  "USDT",
  "PIX",
  "SPEI",
  "CBU_CVU",
  "NEQUI",
  "BANCOLOMBIA_BUTTON",
];

/** Operation statuses worth filtering by (payouts and payins share the list endpoint). */
export const MOVEMENT_OPERATION_STATUSES = [
  "PROCESSING",
  "PAID",
  "CREATED",
  "FAILED",
  "CONFIRMED",
  "PENDING",
  "REVERSED",
];

export const MOVEMENT_INCIDENT_FILTERS: MovementIncidentFilter[] = [
  "open",
  "any",
  "none",
  "pending_report",
  "reported",
  "in_progress",
  "provider_refunded",
  "resolved",
  "dismissed",
];

/**
 * One-click views for the two cases staff come here for: a Bre-B payment a client says never arrived, and an
 * OTC purchase still waiting on the desk; plus the incidents that still wait on someone.
 */
export const MOVEMENT_PRESETS = {
  breb_paid: { rail: "BREB", direction: "D", operationStatus: "PAID" },
  otc_processing: { rail: "USDT", direction: null, operationStatus: "PROCESSING" },
  open_incidents: { incident: "open" },
} as const satisfies Record<string, Partial<WalletMovementsFilters>>;
export type MovementPreset = keyof typeof MOVEMENT_PRESETS;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A real local calendar day (`YYYY-MM-DD`), or null. The shape is not enough: `Date` rolls "2026-02-30" over
 * to March 2 and makes "2026-99-99" an Invalid Date, whose `toISOString()` throws during render.
 */
export function calendarDay(value: string): Date | null {
  const date = isoToDate(value);
  return date && dateToIso(date) === value ? date : null;
}

/** Filters and page as a link wrote them; anything malformed is dropped rather than sent. */
export function readMovementsUrl(params: URLSearchParams): { filters: WalletMovementsFilters; page: number } {
  const get = (key: keyof typeof MOVEMENTS_URL_PARAMS) => params.get(MOVEMENTS_URL_PARAMS[key])?.trim() ?? "";
  const pick = <T extends string>(value: string, allowed: readonly T[]): T | null =>
    (allowed as readonly string[]).includes(value) ? (value as T) : null;
  const money = (value: string) => (MONEY_INPUT_PATTERN.test(value) ? value : "");
  const page = Number(get("page"));
  return {
    filters: {
      search: get("search").slice(0, MOVEMENTS_SEARCH_MAX),
      organizationId: UUID.test(get("organizationId")) ? get("organizationId") : null,
      currency: /^[A-Z]{3,8}$/.test(get("currency")) ? get("currency") : null,
      rail: pick(get("rail"), MOVEMENT_RAILS),
      direction: pick(get("direction"), ["C", "D"] as const),
      operationStatus: pick(get("operationStatus"), MOVEMENT_OPERATION_STATUSES),
      incident: pick(get("incident"), MOVEMENT_INCIDENT_FILTERS),
      from: calendarDay(get("from")) ? get("from") : "",
      to: calendarDay(get("to")) ? get("to") : "",
      minAmount: money(get("minAmount")),
      maxAmount: money(get("maxAmount")),
    },
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** Whether the filters are exactly a preset's (and nothing else). */
export function activePreset(filters: WalletMovementsFilters): MovementPreset | null {
  for (const [name, preset] of Object.entries(MOVEMENT_PRESETS) as [
    MovementPreset,
    Partial<WalletMovementsFilters>,
  ][]) {
    const expected = { ...EMPTY_MOVEMENTS_FILTERS, ...preset };
    if (
      (Object.keys(expected) as (keyof WalletMovementsFilters)[]).every(
        (key) => filters[key] === expected[key],
      )
    ) {
      return name;
    }
  }
  return null;
}

/**
 * The list request for some filters: local days become their first and last instant; a day that is not one is
 * left out.
 */
export function movementsQuery(
  filters: WalletMovementsFilters,
  options: { excludeRoot: boolean; skip: number; limit: number },
): ListPaymentMovementsParams {
  const from = calendarDay(filters.from);
  const to = calendarDay(filters.to);
  to?.setHours(23, 59, 59, 999);
  return {
    search: filters.search.trim() || undefined,
    organizationId: filters.organizationId ?? undefined,
    currency: filters.currency ?? undefined,
    rail: filters.rail ?? undefined,
    direction: filters.direction ?? undefined,
    operationStatus: filters.operationStatus ?? undefined,
    incident: filters.incident ?? undefined,
    from: from?.toISOString(),
    to: to?.toISOString(),
    minAmount: MONEY_INPUT_PATTERN.test(filters.minAmount) ? filters.minAmount : undefined,
    maxAmount: MONEY_INPUT_PATTERN.test(filters.maxAmount) ? filters.maxAmount : undefined,
    excludeRoot: options.excludeRoot,
    skip: options.skip,
    limit: options.limit,
  };
}
